/** Home page — nav, contact form, Kickstarter tease. */

type RuntimeConfig = {
 apiEndpoint?: string;
};

type FieldName = "name" | "email" | "phone" | "message";

const REQUIRED_FIELDS: ReadonlyArray<FieldName> = ["name", "email", "message"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let cachedConfig: RuntimeConfig | null = null;
let configLoaded = false;

async function loadConfig(): Promise<RuntimeConfig> {
 if (cachedConfig) return cachedConfig;
 try {
 const res = await fetch("config.json", { cache: "no-store" });
 if (!res.ok) throw new Error(`config.json -> ${res.status}`);
 cachedConfig = (await res.json()) as RuntimeConfig;
 configLoaded = true;
 } catch (err) {
 console.warn("[ingram] failed to load runtime config", err);
 cachedConfig = {};
 configLoaded = false;
 }
 return cachedConfig;
}

function setupNavToggle(): void {
 const toggle = document.querySelector<HTMLButtonElement>(".nav__toggle");
 const menu = document.querySelector<HTMLElement>("#nav-menu");
 if (!toggle || !menu) return;

 const setOpen = (open: boolean) => {
 menu.dataset.open = open ? "true" : "false";
 toggle.setAttribute("aria-expanded", open ? "true" : "false");
 };
 setOpen(false);

 toggle.addEventListener("click", () => {
 const isOpen = menu.dataset.open === "true";
 setOpen(!isOpen);
 });

 menu.querySelectorAll<HTMLAnchorElement>("a.nav__link").forEach((link) => {
 link.addEventListener("click", () => setOpen(false));
 });

 const mql = window.matchMedia("(min-width: 769px)");
 mql.addEventListener("change", (e) => {
 if (e.matches) setOpen(false);
 });
}

function setupComingSoonButtons(): void {
 document
 .querySelectorAll<HTMLAnchorElement>('a[data-coming-soon="true"]')
 .forEach((el) => {
 el.setAttribute("role", "button");
 el.setAttribute("aria-disabled", "true");
 el.title = "Kickstarter campaign launching soon";
 el.addEventListener("click", (ev) => {
 ev.preventDefault();
 });
 });
}

function setError(form: HTMLFormElement, field: FieldName, message: string): void {
 const input = form.elements.namedItem(field);
 if (input instanceof HTMLElement) {
 input.setAttribute("aria-invalid", message ? "true" : "false");
 }
 const slot = form.querySelector<HTMLElement>(`[data-error-for="${field}"]`);
 if (slot) slot.textContent = message;
}

function clearErrors(form: HTMLFormElement): void {
 (["name", "email", "phone", "message"] as const).forEach((f) => setError(form, f, ""));
}

type Payload = { name: string; email: string; phone: string; message: string };

function readPayload(form: HTMLFormElement): Payload {
 const data = new FormData(form);
 const get = (k: FieldName): string => String(data.get(k) ?? "").trim();
 return {
 name: get("name"),
 email: get("email"),
 phone: get("phone"),
 message: get("message"),
 };
}

function validate(form: HTMLFormElement, payload: Payload): boolean {
 let ok = true;
 for (const field of REQUIRED_FIELDS) {
 if (!payload[field]) {
 setError(form, field, "Required.");
 ok = false;
 }
 }
 if (payload.email && !EMAIL_RE.test(payload.email)) {
 setError(form, "email", "Enter a valid email address.");
 ok = false;
 }
 if (payload.message && payload.message.length < 10) {
 setError(form, "message", "Please share at least a sentence (10+ characters).");
 ok = false;
 }
 return ok;
}

function setStatus(
 el: HTMLElement,
 state: "idle" | "loading" | "success" | "error",
 message: string,
): void {
 el.dataset.state = state;
 el.textContent = message;
}

function setupContactForm(): void {
 const form = document.querySelector<HTMLFormElement>("#contact-form");
 if (!form) return;

 const status = form.querySelector<HTMLElement>("#form-status");
 const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');

 form.addEventListener("submit", async (ev) => {
 ev.preventDefault();
 clearErrors(form);
 if (!status || !submitBtn) return;

 const payload = readPayload(form);

 // Honeypot: bots that fill hidden "company" get a fake success.
 const company = String(new FormData(form).get("company") ?? "").trim();
 if (company) {
 form.reset();
 setStatus(
 status,
 "success",
 "Thanks. Your inquiry is on its way. We'll be in touch within 2 business days.",
 );
 return;
 }

 if (!validate(form, payload)) {
 setStatus(status, "error", "Please correct the highlighted fields and try again.");
 return;
 }

 const cfg = await loadConfig();
 if (!configLoaded) {
 setStatus(
 status,
 "error",
 "Form is not configured yet. Please email us directly (see footer).",
 );
 return;
 }

 submitBtn.disabled = true;
 setStatus(status, "loading", "Sending your inquiry…");

 try {
 const base = (cfg.apiEndpoint ?? "").replace(/\/$/, "");
 const res = await fetch(`${base}/contact`, {
 method: "POST",
 headers: { "content-type": "application/json" },
 body: JSON.stringify(payload),
 });

 if (!res.ok) {
 const text = await res.text().catch(() => "");
 throw new Error(`HTTP ${res.status} ${text}`);
 }

 form.reset();
 setStatus(
 status,
 "success",
 "Thanks. Your inquiry is on its way. We'll be in touch within 2 business days.",
 );
 } catch (err) {
 console.error("[ingram] contact form submit failed", err);
 setStatus(
 status,
 "error",
 "Something went wrong sending your message. Please try again in a moment.",
 );
 } finally {
 submitBtn.disabled = false;
 }
 });
}

function init(): void {
 setupNavToggle();
 setupComingSoonButtons();
 setupContactForm();
}

if (document.readyState === "loading") {
 document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
 init();
}
